import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { AuxEntity, getListadoGranos } from "@/services/aux-entities.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ProductionGrainForm } from "../components/ProductionGrainForm";
import { hasEmptyProductionGrainFormValues } from "../utils/production-grain-form";
import { createProductionGrainHandler } from "./schema/handler-functions";
import {
  ProductionGrainCreationForm,
  productionGrainCreationSchema,
} from "./schema/production-grain-creation.schema";

export const ProductionGrainCreationPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [grains, setGrains] = useState<AuxEntity[] | null>([]);
  const [loadingGrains, setLoadingGrains] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchGrains = async () => {
    setLoadingGrains(true);
    const response = await getListadoGranos();
    setGrains(response);
    setLoadingGrains(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchGrains();
    }
  }, [isActive]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ProductionGrainCreationForm>({
    resolver: zodResolver(productionGrainCreationSchema),
    defaultValues: {
      nombre: "",
      aplicaPPMB: undefined,
      granoAdmis: "",
      sumaHA: undefined,
    },
  });

  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    return hasEmptyProductionGrainFormValues(watchedValues);
  }, [watchedValues]);

  const handleCancel = () => {
    navigate(AppRoutes.PRODUCTION_GRAINS);
  };

  const onSubmit = async (values: ProductionGrainCreationForm) => {
    await createProductionGrainHandler({
      values,
      setLoadingCreate,
      navigate,
      fetchPermissions,
    });
  };

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      flexDir="column"
      paddingTop="20px"
      gap="12px"
    >
      <SectionHeader
        title="Agregar grano productivo"
        addButtonLabel="Guardar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingCreate}
        loading={loadingCreate}
      />

      <ProductionGrainForm
        control={control}
        errors={errors}
        grains={grains}
        isActive={isActive}
        loading={loadingCreate}
        loadingGrains={loadingGrains}
        comboboxKey={
          grains?.length
            ? "production-grain-create-loaded"
            : "production-grain-create-empty"
        }
      />
    </Flex>
  );
};
